import { AUDIT_STEPS } from "./audit.js";

// What a browser keeps about a client's private page: only which page it last
// opened. The sign-in itself is a cookie the scripts here cannot read.
const store = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export function remember(slug) {
  try {
    store()?.setItem("nomus-room-last", slug);
  } catch {
    // Without storage, paying returns to the general thank-you page.
  }
}
export function forgetRoom(slug) {
  try {
    store()?.removeItem(`nomus-room-${slug}`);
    if (store()?.getItem("nomus-room-last") === slug)
      store()?.removeItem("nomus-room-last");
  } catch {
    // Nothing stored.
  }
}
// The page this browser last opened.
export function lastRoom() {
  try {
    return store()?.getItem("nomus-room-last") || "";
  } catch {
    return "";
  }
}
// Browsers that opened a page before sign-in existed kept its access code.
// Hand it over once, to be swapped for a session, and keep it no longer.
export function legacyCode(slug) {
  try {
    const kept = JSON.parse(store()?.getItem(`nomus-room-${slug}`) || "null");
    store()?.removeItem(`nomus-room-${slug}`);
    return kept?.c || "";
  } catch {
    return "";
  }
}

// Just booked: the page offers the way back until they have taken it.
const fresh = (slug) => `nomus-room-new-${slug}`;
// `how` is "mailed" when a sign-in link is in their inbox, otherwise the access
// code they were given instead.
export function markFresh(slug, how) {
  try {
    store()?.setItem(fresh(slug), how || "");
  } catch {
    // The way back is still under "Access" on the page.
  }
}
export function isFresh(slug) {
  try {
    return store()?.getItem(fresh(slug)) || "";
  } catch {
    return "";
  }
}
export function clearFresh(slug) {
  try {
    store()?.removeItem(fresh(slug));
  } catch {
    // Nothing stored.
  }
}

// Where a page stands. Before payment there are two things to do, in order.
// After it, day 1 is the day of the walkthrough, and the fourteen days are
// counted from there in the reader's own time zone. Until that day comes the
// page is "before day 1".
const DAY = 86400e3;
const localDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());
// Day 14, or the next working day when it falls on a weekend.
function dueDay(start) {
  const due = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate() + 13,
  );
  const shift = { 6: 2, 0: 1 }[due.getDay()] || 0;
  due.setDate(due.getDate() + shift);
  return due;
}
const PAUSE_DAYS = 14;
export function standing(room, now = new Date()) {
  const a = room.agreement;
  const phase = a && !a.signed ? "sign" : a && !room.paid ? "pay" : "work";
  // A pause stops the count; a closed Audit stays at the day it closed.
  const pauses = room.pauses || [];
  const open = pauses.find((p) => !p.to);
  const more = {
    closed: room.closed || null,
    paused: open
      ? {
          until: new Date(Date.parse(open.from) + PAUSE_DAYS * DAY),
          reason: open.reason || "",
          by: open.by || "",
        }
      : null,
  };
  if (!room.paid?.at) {
    const stage = Math.min(room.stage ?? 0, AUDIT_STEPS.length - 1);
    return {
      phase,
      day: room.day ?? null,
      stage,
      due: null,
      start: null,
      ...more,
    };
  }
  const when = room.walk?.when ? new Date(room.walk.when) : null;
  if (!when || Number.isNaN(when.getTime()))
    return {
      phase,
      before: true,
      day: null,
      stage: 1,
      due: null,
      start: null,
      ...more,
    };
  const start = localDay(when);
  const today = localDay(
    room.closed ? new Date(room.closed.at) : new Date(now),
  );
  // Whole days that were not counted, from day 1 on.
  const off = pauses.reduce((days, p) => {
    const from = Math.max(localDay(new Date(p.from)), start);
    const to = p.to ? localDay(new Date(p.to)) : today;
    return days + Math.max(0, Math.round((to - from) / DAY));
  }, 0);
  const due = dueDay(
    new Date(start.getFullYear(), start.getMonth(), start.getDate() + off),
  );
  const passed = Math.round((today - start) / DAY) + 1 - off;
  if (passed < 1)
    return { phase, before: true, day: null, stage: 1, due, start, ...more };
  const day = Math.min(passed, 14);
  // The steps of the Audit, by the day they belong to.
  const stage = room.result
    ? passed > 14
      ? 7
      : 6
    : day === 1
      ? 2
      : day <= 5
        ? 3
        : day <= 7
          ? 4
          : day <= 13
            ? 5
            : 6;
  return { phase, day, stage, due, start, ...more };
}
